/**
 * Vendor API Service
 * All API calls for Vendor Management (Phase 2)
 * Follows the procurement module API patterns
 */

import api from "../../../services/api";

const BASE_PATH = "/api/v1/procurement";

// ==================== VENDORS ====================

/**
 * Get all vendors with pagination and filtering
 * GET /api/v1/procurement/vendors
 * @param {Object} params - Query parameters { isActive, search, page, limit }
 */
export const getVendors = async (params = {}) => {
  const response = await api.get(`${BASE_PATH}/vendors`, { params });
  return response.data;
};

/**
 * Get all active vendors (for dropdowns)
 * GET /api/v1/procurement/vendors/active/list
 */
export const getActiveVendors = async () => {
  const response = await api.get(`${BASE_PATH}/vendors/active/list`);
  return response.data;
};

/**
 * Search vendors by name, email, or phone
 * GET /api/v1/procurement/vendors/search
 * @param {string} searchTerm - Search query (min 2 characters)
 */
export const searchVendors = async (searchTerm) => {
  const response = await api.get(`${BASE_PATH}/vendors/search`, {
    params: { q: searchTerm },
  });
  return response.data;
};

/**
 * Get vendor statistics
 * GET /api/v1/procurement/vendors/stats
 */
export const getVendorStats = async () => {
  const response = await api.get(`${BASE_PATH}/vendors/stats`);
  return response.data;
};

/**
 * Get vendor by ID
 * GET /api/v1/procurement/vendors/:id
 * @param {string} id - Vendor ID
 */
export const getVendorById = async (id) => {
  const response = await api.get(`${BASE_PATH}/vendors/${id}`);
  return response.data;
};

/**
 * Create a new vendor
 * POST /api/v1/procurement/vendors
 * @param {Object} data - Vendor data { name, email, phone, gstNumber, address }
 */
export const createVendor = async (data) => {
  const response = await api.post(`${BASE_PATH}/vendors`, data);
  return response.data;
};

/**
 * Update an existing vendor
 * PUT /api/v1/procurement/vendors/:id
 * @param {string} id - Vendor ID
 * @param {Object} data - Updated vendor data
 */
export const updateVendor = async (id, data) => {
  const response = await api.put(`${BASE_PATH}/vendors/${id}`, data);
  return response.data;
};

/**
 * Toggle vendor active/inactive status
 * POST /api/v1/procurement/vendors/:id/toggle-status
 * @param {string} id - Vendor ID
 */
export const toggleVendorStatus = async (id) => {
  const response = await api.post(`${BASE_PATH}/vendors/${id}/toggle-status`);
  return response.data;
};

/**
 * Delete a vendor (soft delete)
 * DELETE /api/v1/procurement/vendors/:id
 * @param {string} id - Vendor ID
 */
export const deleteVendor = async (id) => {
  const response = await api.delete(`${BASE_PATH}/vendors/${id}`);
  return response.data;
};

// Export all functions as named exports
export default {
  getVendors,
  getActiveVendors,
  searchVendors,
  getVendorStats,
  getVendorById,
  createVendor,
  updateVendor,
  toggleVendorStatus,
  deleteVendor,
};
